import { useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";
import { toast } from "sonner";
import type { Database } from "@/integrations/supabase/types";
import {
  type FeatureFlagKey,
  isFeatureFlagEnabled,
} from "@/lib/feature-flags";

type FeatureFlagRow = Database["public"]["Tables"]["feature_flags"]["Row"];

interface UseFeatureFlagsOptions {
  /** When set (e.g. admin UI), reads flags for this workspace instead of the active one. */
  workspaceId?: string | null;
}

export function useFeatureFlags(options?: UseFeatureFlagsOptions) {
  const { currentWorkspace } = useWorkspace();
  const workspaceId =
    options?.workspaceId ?? currentWorkspace?.id ?? null;

  const query = useQuery({
    queryKey: ["feature_flags", workspaceId],
    queryFn: async (): Promise<FeatureFlagRow[]> => {
      if (!workspaceId) return [];

      const { data, error } = await supabase
        .from("feature_flags")
        .select("*")
        .eq("workspace_id", workspaceId);

      if (error) throw error;
      return data ?? [];
    },
    enabled: !!workspaceId,
  });

  const byKey = useMemo(() => {
    const map = new Map<string, FeatureFlagRow>();
    for (const row of query.data ?? []) {
      map.set(row.key, row);
    }
    return map;
  }, [query.data]);

  const isEnabled = useCallback(
    (key: FeatureFlagKey): boolean => {
      return isFeatureFlagEnabled(byKey.get(key));
    },
    [byKey],
  );

  return {
    workspaceId,
    rows: query.data ?? [],
    byKey,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    isEnabled,
    refetch: query.refetch,
  };
}

export function useUpsertFeatureFlag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      workspaceId: string;
      key: FeatureFlagKey;
      enabled: boolean;
    }) => {
      const row: Database["public"]["Tables"]["feature_flags"]["Insert"] = {
        workspace_id: payload.workspaceId,
        key: payload.key,
        enabled: payload.enabled,
      };

      const { error } = await supabase
        .from("feature_flags")
        .upsert(row, { onConflict: "workspace_id,key" });

      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["feature_flags", variables.workspaceId],
      });
      toast.success("Feature flag updated");
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });
}
